import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditDefaultPlanMutation } from "@modules/defaultPlans/services/mutations";
import {
  DefaultPlanFormValues,
  defaultPlanSchema,
} from "@modules/defaultPlans/validations";
import { listDefaultPlansQueryKey } from "@modules/defaultPlans/services/queries/listDefaultPlans/query";

export function useEditDefaultPlanModal(
  modalRef: RefObject<ModalRef>,
  onComplete: () => void
) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<DefaultPlanFormValues>({
    resolver: zodResolver(defaultPlanSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditDefaultPlanMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listDefaultPlansQueryKey,
        refetchType: "all",
      });

      open?.({
        type: "success",
        message: translate("default_plan_admin.success.edit"),
      });

      modalRef.current?.close();
      onComplete?.();
    },
  });

  const handleSubmit = async (data: DefaultPlanFormValues) => {
    const defaultplanId = modalRef.current?.getId();
    await mutateAsync({ id: defaultplanId as string, data });
  };

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
